import React, {useState, useEffect, useRef} from 'react'
import {motion} from 'framer-motion'
import {OpenAIService} from '../../services/openaiService'
import {SupabaseService} from '../../services/supabaseService'
import {CreditService} from '../../services/creditService'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {FiZap, FiLoader, FiCheck, FiAlertCircle, FiRefreshCw, FiEdit, FiCopy, FiMail, FiInfo, FiDatabase, FiX} = FiIcons

const DirectoryContentGenerator = ({opportunity, user, onPurchaseClick, defaultValues, onSendEmail, onStatusChange}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [insufficientCredits, setInsufficientCredits] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [content, setContent] = useState('')
  const [subject, setSubject] = useState('')
  const [hasContent, setHasContent] = useState(false)
  const [copiedField, setCopiedField] = useState(null)
  const [contentSaving, setContentSaving] = useState(false)
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const [sending, setSending] = useState(false)
  const [toastVisible, setToastVisible] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [emailLogs, setEmailLogs] = useState([])
  const [logModalOpen, setLogModalOpen] = useState(false)
  // Moved manual entry hooks to top-level before any conditional returns
  const [manualOpen, setManualOpen] = useState(false)
  const [manualSubject, setManualSubject] = useState('')
  const [manualBody, setManualBody] = useState('')
  const [savingManual, setSavingManual] = useState(false)
  const subjectTextareaRef = useRef(null)
  const contentTextareaRef = useRef(null)

  useEffect(() => {
    // Load existing content when component mounts
    loadExistingContent()
    // Load existing logs for this link
    loadEmailLogs()
  }, [opportunity.id])

  const updateTextareaHeight = (element) => {
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = (element.scrollHeight) + 'px';
  };

  useEffect(() => {
    // Update textarea heights when content changes
    if (hasContent) {
      if (subjectTextareaRef.current) {
        updateTextareaHeight(subjectTextareaRef.current);
      }
      if (contentTextareaRef.current) {
        updateTextareaHeight(contentTextareaRef.current);
      }
    }
  }, [subject, content, hasContent]);

  const loadEmailLogs = async () => {
    try {
      const { data, error } = await SupabaseService.supabase
        .from('email_logs')
        .select('to_email, subject, sent_at')
        .eq('user_id', user.id)
        .eq('link_id', opportunity.id)
        .order('sent_at', { ascending: false })
      if (!error) setEmailLogs(data || [])
    } catch (e) {
      // noop
    }
  }

  const loadExistingContent = async () => {
    try {
      const existingData = await SupabaseService.getDirectoryGeneratedContent(
        user.id,
        opportunity.id
      )
      if (existingData) {
        // Check if the content includes subject line data
        if (existingData.subject) {
          setSubject(existingData.subject)
          setContent(existingData.content || '')
        } else {
          // For backward compatibility - extract subject if it exists
          let extractedSubject = "No subject line detected";
          let messageBody = existingData.content || '';
          if (opportunity.type === 'Guest Post' || opportunity.type === 'Resource Page') {
            const lines = messageBody.split('\n');
            const subjectLineIndex = lines.findIndex(line => line.trim().toLowerCase().startsWith('subject:'));
            if (subjectLineIndex !== -1) {
              extractedSubject = lines[subjectLineIndex].replace(/^subject:\s*/i, '').trim();
              // Remove the subject line from the content
              const contentLines = [...lines];
              contentLines.splice(subjectLineIndex, 1);
              messageBody = contentLines.join('\n').trim();
              // Update the database with the extracted subject
              await SupabaseService.saveDirectoryGeneratedContent(
                user.id,
                opportunity.id,
                messageBody,
                extractedSubject
              )
            }
          }
          setSubject(extractedSubject)
          setContent(messageBody)
        }
        setHasContent(true)
        
        // Update textarea heights after setting content
        setTimeout(() => {
          if (subjectTextareaRef.current) {
            updateTextareaHeight(subjectTextareaRef.current);
          }
          if (contentTextareaRef.current) {
            updateTextareaHeight(contentTextareaRef.current);
          }
        }, 0);
      }
    } catch (error) {
      console.error('Error loading existing content:', error)
    }
  }

  useEffect(() => {
    let t
    if (toastVisible) {
      t = setTimeout(() => setToastVisible(false), 5000)
    }
    return () => t && clearTimeout(t)
  }, [toastVisible])

  const generateContent = async () => {
    setLoading(true)
    setError('')
    setInsufficientCredits(false)
    setRegenerating(hasContent)
    try {
      console.log('Starting content generation for directory opportunity:', opportunity.id)
      // First check if user has enough credits (0.0025) and deduct the amount
      const creditUsed = await CreditService.spendFractionalCredit(user.id)
      if (creditUsed) {
        console.log('Credit deduction failed - insufficient credits')
        setInsufficientCredits(true)
        throw new Error('Insufficient credits. You need at least 0.0025 credits to generate content.')
      }

      console.log('Credit deduction successful, generating content...')
      // Fetch user personalization data
      const userData = await SupabaseService.getUserPersonalization(user.id, null);

      // Fetch Link Directory–specific default_custom_prompt override
      let userDataWithPrompt = {...userData}
      try {
        const { data: defaultsRow } = await SupabaseService.supabase
          .from('user_outreach_defaults_k9l8x3')
          .select('default_custom_prompt')
          .eq('user_id', user.id)
          .maybeSingle()
        userDataWithPrompt.default_custom_prompt = (defaultsRow?.default_custom_prompt || '')
      } catch (e) {
        userDataWithPrompt.default_custom_prompt = ''
      }

      // If credits were successfully deducted, generate content
      const generatedContent = await OpenAIService.generateDirectoryContent(
        opportunity.link_opportunity,
        opportunity.type,
        opportunity.contact,
        opportunity.niche,
        userDataWithPrompt
      )

      console.log('Content generated, saving to database...')
      // Extract subject line if it exists (for Guest Post and Resource Page types)
      let extractedSubject = "No subject line detected";
      let messageBody = generatedContent;
      if (opportunity.type === 'Guest Post' || opportunity.type === 'Resource Page') {
        const lines = generatedContent.split('\n');
        const subjectLineIndex = lines.findIndex(line => line.trim().toLowerCase().startsWith('subject:'));
        if (subjectLineIndex !== -1) {
          extractedSubject = lines[subjectLineIndex].replace(/^subject:\s*/i, '').trim();
          // Remove the subject line from the content
          const contentLines = [...lines];
          contentLines.splice(subjectLineIndex, 1);
          messageBody = contentLines.join('\n').trim();
        }
      }

      // Save to Supabase with subject line
      await SupabaseService.saveDirectoryGeneratedContent(
        user.id,
        opportunity.id,
        messageBody,
        extractedSubject
      )

      // Update local state
      setSubject(extractedSubject)
      setContent(messageBody)
      setHasContent(true)
      
      // Update textarea heights after setting content
      setTimeout(() => {
        if (subjectTextareaRef.current) {
          updateTextareaHeight(subjectTextareaRef.current);
        }
        if (contentTextareaRef.current) {
          updateTextareaHeight(contentTextareaRef.current);
        }
      }, 0);
    } catch (error) {
      console.error('Content generation error:', error)
      if (!insufficientCredits) {
        setError('Failed to generate content. This action requires 0.0025 credits.')
      } else {
        setInsufficientCredits(true)
        setError('Insufficient credits. You need at least 0.0025 credits to generate content.')
      }
    } finally {
      setLoading(false)
      setRegenerating(false)
    }
  }

  const handleContentChange = async (newContent) => {
    setContent(newContent);
    setContentSaving(true);
    
    try {
      // Save to database after a small debounce (500ms)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await SupabaseService.saveDirectoryGeneratedContent(
        user.id,
        opportunity.id,
        newContent,
        subject
      );
    } catch (error) {
      console.error('Error saving content:', error);
    } finally {
      setContentSaving(false);
    }
  };

  const handleSubjectChange = async (newSubject) => {
    setSubject(newSubject);
    setContentSaving(true);
    
    try {
      // Save to database after a small debounce (500ms)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await SupabaseService.saveDirectoryGeneratedContent(
        user.id,
        opportunity.id,
        content,
        newSubject
      );
    } catch (error) {
      console.error('Error saving subject:', error);
    } finally {
      setContentSaving(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const isEmailContact = (contact) => {
    return contact && contact.includes('@') && !contact.startsWith('http')
  }

  const canSendEmail = () => {
    const hasSubject = subject && subject.trim() !== '' && subject !== 'No subject line detected'
    const hasBody = content && content.trim() !== ''
    const hasWebhook = (defaultValues?.webhook_url || '').trim() !== ''
    return hasSubject && hasBody && hasWebhook && isEmailContact(opportunity.contact)
  }

  const handleSendEmailClick = async () => {
    if (!canSendEmail()) return
    const emails = [opportunity.contact]
    const sentAtIso = new Date().toISOString()
    setSending(true)
    try {
      // Reuse the existing send email function from Results via prop
      await onSendEmail(emails, subject, content)
      // Log each send with link_id and null search/result ids
      await SupabaseService.supabase
        .from('email_logs')
        .insert([{ user_id: user.id, search_id: null, result_id: null, link_id: String(opportunity.id), to_email: opportunity.contact, subject, sent_at: sentAtIso }])
      // Refresh logs and show toast
      await loadEmailLogs()
      const timeString = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      setToastMessage(`Sent emails to ${emails.length} contact${emails.length > 1 ? 's' : ''} at ${timeString}`)
      setToastVisible(true)
      // Auto update directory action status to Submitted for this user/opportunity
      try {
        const { data: existing } = await SupabaseService.supabase
          .from('user_link_directory_actions')
          .select('id')
          .eq('user_id', user.id)
          .eq('link_id', opportunity.id)
          .maybeSingle()
        const timestamp = new Date().toISOString()
        if (existing) {
          await SupabaseService.supabase
            .from('user_link_directory_actions')
            .update({ status: 'Submitted', updated_at: timestamp })
            .eq('id', existing.id)
        } else {
          await SupabaseService.supabase
            .from('user_link_directory_actions')
            .insert([{ user_id: user.id, link_id: opportunity.id, status: 'Submitted', created_at: timestamp, updated_at: timestamp }])
        }
        if (onStatusChange) {
          onStatusChange(opportunity.id, 'Submitted')
        }
      } catch (e) {
        // noop
      }
    } catch (e) {
      console.error('Error sending directory email:', e)
    } finally {
      setSending(false)
    }
  }

  // If content has already been generated, show a success message
  if (hasContent) {
    return (
      <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg relative">
        {/* Toast notification (matches Search Results) */}
        {toastVisible && (
          <motion.div
            initial={{opacity: 0, y: -50}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: -50}}
            className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 flex items-center bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg max-w-sm"
          >
            <img src="https://i.imghippo.com/files/GkAk1182MIE.webp" alt="K9 Links Dog" className="w-12 h-12 mr-3" />
            <p className="text-gray-800 dark:text-white font-medium">{toastMessage}</p>
          </motion.div>
        )}

        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Generated Content:
          </span>
          <div className="flex items-center gap-2">
            {contentSaving && (
              <span className="text-xs text-blue-600 dark:text-blue-400">
                Saving...
              </span>
            )}
          </div>
        </div>

        {(opportunity.type === 'Guest Post' || opportunity.type === 'Resource Page') && hasContent && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                Subject Line:
              </label>
              <button
                onClick={() => copyToClipboard(subject, 'subject')}
                className="flex items-center text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 no-border"
              >
                <SafeIcon
                  icon={copiedField === 'subject' ? FiCheck : FiCopy}
                  className="w-3 h-3 mr-1"
                />
                {copiedField === 'subject' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <textarea
              ref={subjectTextareaRef}
              value={subject}
              onChange={(e) => handleSubjectChange(e.target.value)}
              className="w-full p-3 border border-gray-200 bg-white rounded dark:bg-gray-600 dark:border-gray-500 dark:text-white text-sm text-gray-800 dark:text-gray-200 resize-y overflow-hidden"
              rows={1}
              style={{height: 'auto', minHeight: '40px'}}
            />
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
              Message Body:
            </label>
            <button
              onClick={() => copyToClipboard(content, 'content')}
              className="flex items-center text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 no-border"
            >
              <SafeIcon
                icon={copiedField === 'content' ? FiCheck : FiCopy}
                className="w-3 h-3 mr-1"
              />
              {copiedField === 'content' ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <textarea
            ref={contentTextareaRef}
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            className="w-full p-3 border border-gray-200 bg-white rounded dark:bg-gray-600 dark:border-gray-500 dark:text-white text-sm text-gray-800 dark:text-gray-200 resize-y min-h-[100px] overflow-hidden"
            style={{height: 'auto'}}
          />
          {hasContent && (
            <div className="mt-3 flex items-center">
              <button 
                onClick={generateContent} 
                disabled={regenerating}
                className="flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 px-1 py-1 no-border"
              >
                <SafeIcon 
                  icon={regenerating ? FiLoader : FiRefreshCw} 
                  className={`w-3 h-3 mr-1 ${regenerating ? 'animate-spin' : ''}`} 
                />
                {regenerating ? 'Regenerating content...' : 'Regenerate content'}
              </button>

              {isEmailContact(opportunity.contact) && (
                <>
                  <button
                    onClick={handleSendEmailClick}
                    disabled={!canSendEmail() || sending}
                    className="ml-3 flex items-center px-2 py-0 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors blue-btn"
                  >
                    <SafeIcon icon={sending ? FiLoader : FiMail} className={`w-4 h-4 mr-2 ${sending ? 'animate-spin' : ''}`} />
                    {sending ? 'Sending...' : 'Send Email'}
                  </button>
                  {!canSendEmail() && !sending && (
                    <div className="pl-3">
                      <div
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400 focus:outline-none no-border"
                        onMouseEnter={() => setTooltipVisible(true)}
                        onMouseLeave={() => setTooltipVisible(false)}
                      >
                        <SafeIcon icon={FiInfo} className="w-5 h-5" />
                      </div>
                      {tooltipVisible && (
                        <div className="absolute z-10 w-72 bg-white dark:bg-gray-800 p-2 rounded shadow-lg border border-gray-200 dark:border-gray-700 text-sm text-left">
                          Both subject and message must be filled and a webhook URL must be set in settings.
                        </div>
                      )}
                    </div>
                  )}

                  {emailLogs.length > 0 && (
                    <button
                      onClick={() => setLogModalOpen(true)}
                      className="flex items-center text-[#D59A4C] hover:text-[#B58C54] ml-3 no-border"
                    >
                      <SafeIcon icon={FiDatabase} className="w-4 h-4 mr-1" />
                      <span className="text-sm font-medium">Email Log</span>
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Email Logs Modal (matches Search Results) */}
        {logModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black opacity-40" onClick={() => setLogModalOpen(false)}></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-lg w-full mx-4 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Email Log</h3>
                <button className="no-border" onClick={() => setLogModalOpen(false)} aria-label="Close">
                  <SafeIcon icon={FiX} className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {emailLogs.map((entry, idx) => (
                  <div key={idx} className="p-2 rounded border border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-300">{new Date(entry.sent_at).toLocaleString()}</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{entry.to_email}</div>
                    <div className="text-sm text-gray-700 dark:text-gray-200">{entry.subject}</div>
                  </div>
                ))}
                {emailLogs.length === 0 && (
                  <div className="text-sm text-gray-600 dark:text-gray-300">No emails logged yet.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const handleSaveManual = async () => {
    if (!manualBody.trim()) return
    setSavingManual(true)
    try {
      const subjectToSave = manualSubject.trim() || 'No subject line detected'
      await SupabaseService.saveDirectoryGeneratedContent(
        user.id,
        opportunity.id,
        manualBody.trim(),
        subjectToSave
      )
      setSubject(subjectToSave)
      setContent(manualBody.trim())
      setHasContent(true)
      setManualOpen(false)
      setManualSubject('')
      setManualBody('')
      setTimeout(() => {
        if (subjectTextareaRef.current) updateTextareaHeight(subjectTextareaRef.current)
        if (contentTextareaRef.current) updateTextareaHeight(contentTextareaRef.current)
      }, 0)
    } finally {
      setSavingManual(false)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-3">
        <motion.button
          onClick={generateContent}
          disabled={loading}
          whileHover={{scale: 1.02}}
          whileTap={{scale: 0.98}}
          className="inline-flex items-center px-2 py-0 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <SafeIcon
            icon={loading ? FiLoader : insufficientCredits ? FiAlertCircle : FiZap}
            className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`}
          />
          {loading ? 'Generating content...' : 'Generate Content'}
        </motion.button>

        <button
          onClick={() => setManualOpen(prev => !prev)}
          className="flex items-center text-xs text-[#D59A4C] hover:text-[#B58C54] transition-colors px-1 py-1 no-border"
        >
          <SafeIcon icon={FiEdit} className="w-4 h-4 mr-1" />
          <span style={{fontWeight: 'bold'}}>Add Content</span>
        </button>
      </div>

      {manualOpen && (
        <div className="mt-3 space-y-3">
          {(opportunity.type === 'Guest Post' || opportunity.type === 'Resource Page') && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Subject Line</label>
              <textarea
                value={manualSubject}
                onChange={(e) => setManualSubject(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent resize-vertical min-h-[40px] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                rows={1}
                style={{height: 'auto'}}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = (e.target.scrollHeight) + 'px';
                }}
              />
            </div>
          )}
          <div className="space-y-1">
            <label className="block text-sm font medium text-gray-700 dark:text-gray-300">Message Body</label>
            <textarea
              value={manualBody}
              onChange={(e) => setManualBody(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent resize-vertical min-h-[100px] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              style={{height: 'auto'}}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = (e.target.scrollHeight) + 'px';
              }}
            />
          </div>
          <div>
            <motion.button
              onClick={handleSaveManual}
              disabled={savingManual || !manualBody.trim()}
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              className="inline-flex items-center px-1 py-0 sm:px-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors text-[#2D3748]"
            >
              <SafeIcon icon={savingManual ? FiLoader : FiCheck} className={`w-4 h-4 mr-2 ${savingManual ? 'animate-spin' : ''}`} />
              Save Content
            </motion.button>
          </div>
        </div>
      )}

      {loading && (
        <div className="mt-2 text-sm text-[#D59A4C] bg-[#D59A4C20] p-2 rounded">
          Please wait, your content will display in one moment...
        </div>
      )}

      {error && (
        <div className="mt-2 text-sm text-red-600 bg-red-50 dark:bg-red-900/30 p-2 rounded">
          {error}
          {insufficientCredits && (
            <span>
              {' '}You must{' '}
              <button
                onClick={onPurchaseClick}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline font-medium no-border"
              >
                buy more credits
              </button>
              .
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default DirectoryContentGenerator
